import React from "react";
import {
  Grid,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import NumberOfRounds from "./NumberOfRounds";
import GroupSize from "./GroupSize";
import NumberOfTables from "./NumberOfTables";
import CreateMatches from "./CreateMatches";
import StartEvent from "./StartEvent";
import MatchWarning from "./MatchWarning";
import SendPromptDialog from "./SendPromptDialog";
import UndoDialog from "./UndoDialog";
import request from "../../../utilities/requests";
import { EventContext } from "../../../contexts/EventContext";
import { MatchContext } from "../../../contexts/MatchContext";
import { toast } from "../../../utilities/SnackBarUtils";

const MatchingControlPanel = ({ attendeeCount, percentages }) => {
  const { pairingEvent, setAttendees, setPercentages, pairingData } =
    React.useContext(EventContext);
  const { getData } = React.useContext(MatchContext);
  const [syncing, setSyncing] = React.useState(false);
  const [disableSync, setDisableSync] = React.useState(true);
  const [promptDialogOpen, setPromptDialogOpen] = React.useState(false);
  const [canUndo, setCanUndo] = React.useState(false);
  const [undoOpen, setUndoOpen] = React.useState(false);

  React.useEffect(() => {
    if (pairingEvent) {
      if (pairingEvent?.event?.status === "scheduled") {
        setDisableSync(false);
      } else {
        setDisableSync(true);
      }
    }
  }, [pairingEvent]);

  React.useEffect(() => {
    if (pairingEvent?.event?.status === "active" && pairingData?.canUndo) {
      setCanUndo(true);
    } else {
      setCanUndo(false);
    }
  }, [pairingEvent, pairingData]);

  const handleSync = async () => {
    setSyncing(true);
    let result = await request(
      `events/eventbrite/syncattendees?eventId=${pairingEvent?.event?._id}`
    );
    if (result.success) {
      toast.success("Attendees synced!");
    }
    await getEventAttendees(pairingEvent?.event?._id);
    setSyncing(false);
  };

  const getEventAttendees = async (eventId) => {
    const result = await request(`pairingevent/attendees?eventId=${eventId}`);
    if (!result.success) {
      return;
    }
    setAttendees(result.attendees);
    setPercentages(result.percentages);
    console.log("attendees set");
  };

  const renderPercentages = () => {
    if (percentages?.boys || percentages?.girls) {
      return (
        <div style={{ display: "flex", flexDirection: "row", gap: 5 }}>
          <Typography variant="caption">Boys: {percentages?.boys}</Typography>
          <Typography variant="caption">{" / "}</Typography>
          <Typography variant="caption">Girls: {percentages?.girls}</Typography>
          <Typography variant="caption">{" / "}</Typography>
          <Typography variant="caption">
            Unknown: {percentages?.unknown}
          </Typography>
        </div>
      );
    }
    return <></>;
  };

  const handleConfirmUndo = async () => {
    const body = { pairingEventId: pairingEvent._id };
    const result = await request("pairingevent/undomatches", "post", body);
    if (result.success) {
      getData();
      toast.success("Undo successful");
    }
    setUndoOpen(false);
  };

  return (
    <Paper height={3} style={{ padding: 20 }}>
      <Grid container direction="column" alignItems="center" spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" align="center">
            {pairingEvent ? pairingEvent.event.name : ""}
          </Typography>
        </Grid>
        <Grid
          item
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="button">
            Attendees: {attendeeCount}/
            {pairingEvent ? pairingEvent.event.capacity : ""}
          </Typography>
          {renderPercentages()}
        </Grid>

        <Grid item xs={12} style={{ height: 50, width: "100%" }}>
          <Grid container direction="row">
            <Grid item xs={6}>
              <Grid container direction="column" spacing={3}>
                {["mixer", "dinner"].includes(
                  pairingEvent?.pairingEventType
                ) ? null : (
                  <Grid item>
                    <NumberOfRounds pairingEvent={pairingEvent} />
                  </Grid>
                )}
                {["group", "dinner"].includes(
                  pairingEvent?.pairingEventType
                ) ? (
                  <Grid item>
                    <GroupSize pairingEvent={pairingEvent} />
                  </Grid>
                ) : (
                  <></>
                )}
                {["mixer", "dinner"].includes(
                  pairingEvent?.pairingEventType
                ) ? null : (
                  <Grid item>
                    <NumberOfTables pairingEvent={pairingEvent} />
                  </Grid>
                )}

                {pairingEvent?.pairingEventType === "mixer" ? (
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPromptDialogOpen(true)}
                    >
                      Send prompt
                    </Button>
                  </Grid>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container direction="column" spacing={3}>
                <Grid item>
                  <Grid container direction="row" spacing={3}>
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={handleSync}
                        style={{ width: 155, height: 36.5, fontSize: "0.8rem" }}
                        disabled={disableSync}
                      >
                        {syncing ? (
                          <CircularProgress style={{ width: 25, height: 25 }} />
                        ) : (
                          "sync attendees"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <CreateMatches />
                </Grid>
                {canUndo ? (
                  <Grid item>
                    <Button onClick={() => setUndoOpen(true)}>
                      <UndoIcon style={{ marginRight: 10 }} />
                      Undo
                    </Button>
                  </Grid>
                ) : null}

                <Grid item>
                  <StartEvent />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={6} style={{ height: 50, width: "100%" }}>
          <MatchWarning />
        </Grid>
      </Grid>
      {/* <SaveMatchesDialog open={showSave} close={handleCloseShowSave} save={handleSave}/> */}
      <SendPromptDialog
        open={promptDialogOpen}
        close={() => setPromptDialogOpen(false)}
      />
      <UndoDialog
        open={undoOpen}
        close={() => setUndoOpen(false)}
        confirm={handleConfirmUndo}
      />
    </Paper>
  );
};

export default MatchingControlPanel;
